import type { NamedFieldProps } from "./Field.js";
import type { ImmutableArray, ImmutableMap, ValueCallback } from "shelving";
import { AllowStringSchema, ArraySchema, assertInstance, getSource, getString, mapArray, omitArrayItems, withArrayItems } from "shelving";
import { Field, useField } from "./Field.js";
import { FieldNotice } from "./FieldNotice.js";

/**
 * Multi-select field: Shows a list of checkboxes so multiple options can be selected at once.
 */
export function CheckboxesField(props: NamedFieldProps): React.ReactElement {
	const field = useField<ImmutableArray<string>>(props);
	const { name, form, schema, value } = field;
	assertInstance(schema, ArraySchema);
	const { allow } = getSource(AllowStringSchema, schema.items);
	return (
		<Field {...field}>
			<CheckboxesList //
				name={name}
				value={value}
				allow={allow}
				onAdd={k => form.publish(name, withArrayItems(form.data[name] ?? schema.value, k))}
				onRemove={k => form.publish(name, omitArrayItems(form.data[name] ?? schema.value, k))}
			/>
			<FieldNotice name={name} />
		</Field>
	);
}

function CheckboxesList<T extends string>({
	name,
	allow,
	mods = "flex-column gap-hairline",
	onAdd,
	onRemove,
	value,
}: {
	allow: ImmutableMap<T, unknown>;
	value?: ImmutableArray<T> | undefined;
	name: string;
	mods?: string | undefined;
	onAdd: ValueCallback<T>;
	onRemove: ValueCallback<T>;
}): React.ReactElement {
	const onChange: React.FormEventHandler<HTMLInputElement> = ({ currentTarget }) => {
		if (currentTarget.checked) onAdd(currentTarget.value as T);
		else onRemove(currentTarget.value as T);
	};
	return (
		<div className={mods} role="group">
			{mapArray(allow, ([k, t]) => (
				<label key={k} className="input space-none">
					<input type="checkbox" name={name} defaultChecked={value?.includes(k)} defaultValue={k} onChange={onChange} />
					<span>{getString(t)}</span>
				</label>
			))}
		</div>
	);
}
